import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import Panel from '../components/ui/Panel'
import Avatar from '../components/ui/Avatar'
import { buttonClasses } from '../components/ui/Button'
import { getShare } from '../lib/api.share'
import type { ShareGame } from '../lib/api.share'
import { distribute, inputFromRound, SOLO } from '../lib/scoring'
import textsShare from '../texts.share'

const PALETTE = ['#f5c451', '#7ed6b8', '#ff9aa2', '#a9c4ff', '#e2b6ff', '#ffd9a0']

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

function cls(n: number): string {
  return n > 0 ? 'text-mint' : n < 0 ? 'text-red' : 'text-text-2'
}

/** Selvstændig kumulativ pointkurve, samme stil som prototypens statistik-skærm. */
function PointsChart({ game }: { game: ShareGame }) {
  const players = game.players
  const n = game.rounds.length
  const scoringPlayers = players.map((p) => ({ userId: p.userId, name: p.firstName }))

  const series: number[][] = players.map(() => [0])
  game.rounds.forEach((round, i) => {
    const dist = distribute(scoringPlayers, i, inputFromRound(round))
    players.forEach((p, k) => {
      const prev = series[k][series[k].length - 1]
      series[k].push(prev + (dist[p.userId] ?? 0))
    })
  })

  const all = series.flat()
  const max = Math.max(10, ...all)
  const min = Math.min(-10, ...all)
  const W = 400
  const H = 200
  const L = 36
  const T = 10
  const B = 24
  const x = (i: number) => L + (i / Math.max(1, n)) * (W - L - 10)
  const y = (v: number) => T + ((max - v) / (max - min)) * (H - T - B)
  const ticks = [max, 0, min].map((v) => Math.round(v))

  return (
    <Panel>
      <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
        {textsShare.share.pointsCurveTitle}
      </span>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={textsShare.share.pointsCurveTitle} className="w-full">
        {ticks.map((v) => (
          <g key={v}>
            <line
              x1={L}
              x2={W - 10}
              y1={y(v)}
              y2={y(v)}
              stroke="currentColor"
              className="text-text-2/25"
              strokeWidth={v === 0 ? 1.5 : 1}
            />
            <text x={L - 6} y={y(v) + 3} textAnchor="end" fontSize="10" className="fill-text-2">
              {v}
            </text>
          </g>
        ))}
        {Array.from({ length: n }, (_, i) => (
          <text key={i} x={x(i + 1)} y={H - 6} textAnchor="middle" fontSize="10" className="fill-text-2">
            {i + 1}
          </text>
        ))}
        {series.map((pts, k) => (
          <polyline
            key={players[k].userId}
            fill="none"
            stroke={PALETTE[k % PALETTE.length]}
            strokeWidth={2}
            points={pts.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
          />
        ))}
      </svg>
      <div className="flex flex-wrap gap-2.5">
        {players.map((p, k) => (
          <span key={p.userId} className="inline-flex items-center gap-1.5 text-[0.8em] text-text-2">
            <i className="inline-block h-[3px] w-2.5" style={{ background: PALETTE[k % PALETTE.length] }} />
            {p.firstName}
          </span>
        ))}
      </div>
    </Panel>
  )
}

function Share() {
  const { token } = useParams()
  const [game, setGame] = useState<ShareGame | null>(null)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!token) {
      setNotFound(true)
      return
    }
    getShare(token)
      .then(setGame)
      .catch(() => setNotFound(true))
  }, [token])

  if (notFound) {
    return (
      <Screen bare center>
        <div className="grid gap-2.5 text-center">
          <h2>{textsShare.share.notFoundTitle}</h2>
          <p className="text-text-2">{textsShare.share.notFoundHint}</p>
          <Link to="/" className={buttonClasses({ variant: 'ghost' })}>
            {textsShare.share.backToStart}
          </Link>
        </div>
      </Screen>
    )
  }

  if (!game) {
    return (
      <Screen bare center>
        <p className="text-text-2">{textsShare.share.loading}</p>
      </Screen>
    )
  }

  const dateLabel = new Date(game.date).toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
  const sorted = [...game.players].sort((a, b) => b.totalScore - a.totalScore)
  const indexOf = (userId: number) => game.players.findIndex((p) => p.userId === userId)
  const nameById = new Map(game.players.map((p) => [p.userId, p.firstName]))

  return (
    <Screen bare>
      <div className="grid gap-1">
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">{dateLabel}</span>
        <h1>{game.name}</h1>
        <span className="flex items-center gap-2 text-[0.85em] text-text-2">
          {game.clubName && <span>{game.clubName}</span>}
          <span className={game.status === 'completed' ? 'text-gold' : 'text-mint'}>
            {game.status === 'completed' ? textsShare.share.completed : textsShare.share.active}
          </span>
        </span>
      </div>

      <Panel>
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
          {textsShare.share.standingTitle}
        </span>
        <div className="grid gap-1.5">
          {sorted.map((p, rank) => (
            <div key={p.userId} className="flex items-center justify-between rounded-xl bg-ink/20 px-3 py-2">
              <span className="flex items-center gap-2.5">
                <b className="w-4 text-center text-text-2">{rank + 1}</b>
                <Avatar name={p.firstName} index={indexOf(p.userId)} />
                {p.firstName}
              </span>
              <span className={`font-mono font-semibold ${cls(p.totalScore)}`}>{fmt(p.totalScore)}</span>
            </div>
          ))}
        </div>
      </Panel>

      {game.rounds.length > 0 && <PointsChart game={game} />}

      <Panel>
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
          {textsShare.share.roundsTitle}
        </span>
        <div className="grid gap-1.5">
          {[...game.rounds].reverse().map((round) => {
            const isSolo = (SOLO as string[]).includes(round.bidType)
            const isSelfPartner = !isSolo && round.partnerId === round.bidderId
            const bidderName = nameById.get(round.bidderId) ?? ''
            const partnerName = !isSolo && !isSelfPartner && round.partnerId ? nameById.get(round.partnerId) ?? '' : ''
            const detail = isSolo
              ? `${round.bidType} · ${round.won ? textsShare.share.won : textsShare.share.lost}`
              : `${round.bidType} i ${(round.trumpSuit ?? '').toLowerCase()} · ${round.tricksTaken} ${textsShare.share.tricksSuffix}`
            return (
              <div key={round.roundNumber} className="flex items-center justify-between gap-2 text-[0.85em]">
                <span className="min-w-0 truncate">
                  {textsShare.share.round(round.roundNumber)} · {bidderName}
                  {partnerName && ` + ${partnerName}`}
                  {isSelfPartner && ' (selv)'} · {detail}
                </span>
                <span className={`flex-none font-mono font-semibold ${cls(round.score)}`}>{fmt(round.score)}</span>
              </div>
            )
          })}
        </div>
      </Panel>

      <p className="text-center text-[0.85em] text-text-2">
        <Link to="/" className="text-text-2">
          {textsShare.share.poweredBy}
        </Link>
      </p>
    </Screen>
  )
}

export default Share
